import React, { useEffect, useState } from "react";
import { Button, ListGroup, Spinner } from "react-bootstrap";
import { GetSubFolderAsync, MoveFilesToFolderAsync } from "../../../../../../services/fileServices/UserDriverService";
import { GetSubFolderResDto, MoveFilesToFolderReq } from "../../../../../../interfaces/userDrive/createUserFolderRequest";
import foldericon from "../../../../../../assets/images/foldericon.svg";
import { useAuth } from "../../../../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { faArrowLeft, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../../../taskPage/RejectTaskModal.css";

interface MoveFileModalProps {
    show: boolean;
    handleClose: () => void;
    selectedFileIds: string[],
    refreshCurrentFolder : () => void;
    sourceFolderId: number | null;
}

const MoveFileModal: React.FC<MoveFileModalProps> = ({ show, handleClose, selectedFileIds, refreshCurrentFolder, sourceFolderId }) => {
    const { currentUser } = useAuth();
    const [folders, setFolders] = useState<GetSubFolderResDto[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
    const [breadcrumb, setBreadcrumb] = useState<Array<{ id: number | null; name: string }>>([]);
    const [selectedFolder, setSelectedFolder] = useState<GetSubFolderResDto | null>(null);
    const [loadingFolders, setLoadingFolders] = useState<boolean>(false);
    const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);

    useEffect(() => {
        if (show) loadRoot();
        else {
            setFolders([]);
            setBreadcrumb([]);
            setCurrentFolderId(null);
            setSelectedFolder(null);
            setLoadingFolders(false);
            setLoadingConfirm(false);
        }
    }, [show]);

    const loadRoot = async () => {
        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;
            setLoadingFolders(true);
            const res = await GetSubFolderAsync(token, null);
            setFolders(res?.data?.subFolders ?? []);
            setCurrentFolderId(null);
            setBreadcrumb([]);
            setSelectedFolder(null);
        } catch (err) {
            console.error("Failed to load root folders:", err);
        } finally {
            setLoadingFolders(false);
        }
    };

    const openFolder = async (folder: GetSubFolderResDto) => {
        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;
            setLoadingFolders(true);
            const res = await GetSubFolderAsync(token, folder.folderID);
            setFolders(res?.data?.subFolders ?? []);
            setCurrentFolderId(folder.folderID);
            setBreadcrumb((prev) => [...prev, { id: folder.folderID, name: folder.name }]);
            setSelectedFolder(null);
        } catch (err) {
            console.error("Failed to open folder:", err);
        } finally {
            setLoadingFolders(false);
        }
    };

    const onBreadcrumbClick = async (index: number) => {
        if (loadingFolders) return;
        if (index < 0) {
            await loadRoot();
            return;
        }
        const target = breadcrumb[index];
        if (!target?.id) return;
        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;
            setLoadingFolders(true);
            const res = await GetSubFolderAsync(token, target.id);
            setFolders(res?.data?.subFolders ?? []);
            setBreadcrumb((prev) => prev.slice(0, index + 1));
            setCurrentFolderId(target.id);
            setSelectedFolder(null);
        } catch (err) {
            console.error("Failed to load breadcrumb folder:", err);
        } finally {
            setLoadingFolders(false);
        }
    };

    const handleClickMove = async () => {
        setLoadingConfirm(true);
        try {
            const targetFolderId = selectedFolder ? selectedFolder.folderID : currentFolderId;

            const token = await currentUser?.getIdToken();
            if (!token || !selectedFileIds) return;

            const payload: MoveFilesToFolderReq = {
                folderId: targetFolderId,
                userFileIds: selectedFileIds.map(f => Number(f))
            }
            const res = await MoveFilesToFolderAsync(token, payload);

            if (res.success) {
                toast.success(res.message);
                refreshCurrentFolder();
                handleClose();
            } else {
                toast.error(res.message);
            }

        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to move the files");
        } finally {
            setLoadingConfirm(false);
        }
    };

    if (!show) return null;

    const targetFolderId = selectedFolder ? selectedFolder.folderID : currentFolderId;

    return (
        <div className="modal-activity-reject-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                handleClose();
            }
        }}>
            <div className="modal-activity-reject-container max-w-[600px]">
                <div className="modal-activity-reject-header">
                    <h2 className="modal-activity-reject-title">Move File</h2>
                    <button
                        className="modal-activity-reject-close"
                        onClick={handleClose}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-[1rem] h-[1rem]" />
                    </button>
                </div>

                <div className="modal-activity-reject-body" style={{ position: "relative", minHeight: 180 }}>
                    {/* Breadcrumb */}
                    <div className="mb-3">
                        {loadingFolders ? (
                            <div className="placeholder-glow">
                                <span className="placeholder col-2 me-1" />
                                <span className="placeholder col-3" />
                            </div>
                        ) : breadcrumb.length > 0 ? (
                            <span onClick={() => !loadingFolders && onBreadcrumbClick(breadcrumb.length - 2)} className="p-0 cursor-pointer">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </span>
                        ) : null}
                    </div>

                    {/* Folder list or placeholders */}
                    <div aria-busy={loadingFolders} style={{ pointerEvents: loadingFolders ? "none" : "auto", opacity: loadingFolders ? 0.7 : 1, maxHeight: '300px', overflowY: 'auto' }}>
                        {loadingFolders ? (
                            <ListGroup>
                                {Array.from({ length: 6 }).map((_, idx) => (
                                    <ListGroup.Item key={idx} className="d-flex align-items-center">
                                        <div className="placeholder rounded" style={{ width: 48, height: 48 }} />
                                        <div className="ms-3 w-100">
                                            <div className="placeholder-glow">
                                                <span className="placeholder col-6" style={{ height: 16 }} />
                                                <span className="placeholder col-4 mt-2" style={{ height: 12, display: "block" }} />
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <ListGroup>
                                {folders.length === 0 && <div className="text-muted">No folders</div>}
                                {folders.map((f) => (
                                    <ListGroup.Item
                                        key={f.folderID}
                                        action
                                        active={selectedFolder?.folderID === f.folderID}
                                        className="d-flex align-items-center justify-content-between"
                                        onClick={() => !loadingFolders && setSelectedFolder(f)}
                                        onDoubleClick={() => !loadingFolders && openFolder(f)}
                                        style={{ cursor: loadingFolders ? "wait" : "pointer" }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div style={{
                                                width: 48,
                                                height: 48,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "#f5f6f8",
                                                borderRadius: 6,
                                                overflow: "hidden"
                                            }}>
                                                <img src={foldericon} alt="folder" style={{ width: 30, height: 30 }} />
                                            </div>
                                            <div style={{ marginLeft: 12 }}>
                                                <div className="shortDescription" title={f.name}>{f.name}</div>
                                                {/* <small className="text-muted">{f.createdDate}</small> */}
                                            </div>
                                        </div>
                                        <div style={{ opacity: 0.5 }}>{">"}</div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </div>
                </div>

                <div className="modal-activity-reject-footer">
                    <button
                        type="button"
                        className="btn btn-secondary cancel-btn"
                        onClick={handleClose}
                        disabled={loadingConfirm}
                    >
                        Close
                    </button>
                    {loadingConfirm ? (
                        <button type="button" className="btn btn-primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="btn-spinner"
                            />
                            Moving...
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-primary submit-btn"
                            disabled={loadingConfirm || loadingFolders || (folders.length === 0 && !selectedFolder && currentFolderId === null) || targetFolderId === sourceFolderId}
                            onClick={handleClickMove}
                            title={targetFolderId === sourceFolderId ? "You cannot move file to same folder" : undefined}
                        >
                            Move Here
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MoveFileModal;